/**
 * Offline Expiry for Sector Operations (R39 A12.a, SO-P9, SO-P10).
 *
 * Maintains a per-device stamp in localStorage: { lastOnlineAt, maxDays }.
 * - lastOnlineAt moves forward only on proof of server contact:
 *   a successful forced token refresh or a snapshot with fromCache === false.
 * - maxDays is the smallest offlineMaxDays among the workspaces the person has opened (default 7).
 *
 * At startup, before any Firestore read, if now - lastOnlineAt > maxDays:
 * - wipe the Firestore IndexedDB cache before it is read (initialize with memoryLocalCache);
 * - clear count drafts;
 * - mark every helper and member outbox entry older than the window EXPIRED;
 * - drop the cached session user.
 */

export const OFFLINE_EXPIRY_STORAGE_KEY = 'timelining_ops_offline_expiry_stamp'
export const WORKSPACE_OFFLINE_DAYS_KEY = 'timelining_ops_workspace_offline_days'
export const SESSION_USER_STORAGE_KEY = 'timelining-session-user'
export const DEFAULT_OFFLINE_MAX_DAYS = 7
export const ONE_DAY_MS = 24 * 60 * 60 * 1000

export interface OfflineExpiryStamp {
  lastOnlineAt: number
  maxDays: number
}

function safeGetStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      return window.localStorage
    }
  } catch {
    // In restricted iframe / security error
  }
  return null
}

function safeGetSessionStorage(): Storage | null {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      return window.sessionStorage
    }
  } catch {
    // ignore
  }
  return null
}

export function getOfflineExpiryStamp(): OfflineExpiryStamp | null {
  const storage = safeGetStorage()
  if (!storage) return null
  try {
    const raw = storage.getItem(OFFLINE_EXPIRY_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as OfflineExpiryStamp
    if (typeof parsed?.lastOnlineAt === 'number' && typeof parsed?.maxDays === 'number') {
      return parsed
    }
  } catch {
    // ignore
  }
  return null
}

export function saveOfflineExpiryStamp(stamp: OfflineExpiryStamp): void {
  const storage = safeGetStorage()
  if (!storage) return
  try {
    storage.setItem(OFFLINE_EXPIRY_STORAGE_KEY, JSON.stringify(stamp))
  } catch {
    // ignore
  }
}

export function clearOfflineExpiryStamp(): void {
  const storage = safeGetStorage()
  if (!storage) return
  try {
    storage.removeItem(OFFLINE_EXPIRY_STORAGE_KEY)
    storage.removeItem(WORKSPACE_OFFLINE_DAYS_KEY)
  } catch {
    // ignore
  }
}

export function getWorkspaceOfflineDaysMap(): Record<string, number> {
  const storage = safeGetStorage()
  if (!storage) return {}
  try {
    const raw = storage.getItem(WORKSPACE_OFFLINE_DAYS_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (parsed && typeof parsed === 'object') {
      return parsed as Record<string, number>
    }
  } catch {
    // ignore
  }
  return {}
}

export function computeSmallestOfflineMaxDays(
  daysMap: Record<string, number> = getWorkspaceOfflineDaysMap(),
): number {
  const values = Object.values(daysMap).filter(
    (v) => typeof v === 'number' && !Number.isNaN(v) && v > 0,
  )
  if (values.length === 0) {
    return DEFAULT_OFFLINE_MAX_DAYS
  }
  return Math.min(...values)
}

/**
 * Records the offlineMaxDays for a workspace that was opened.
 * Recomputes the smallest maxDays across all opened workspaces and updates the stamp.
 */
export function recordWorkspaceOfflineMaxDays(workspaceId: string, days: number): number {
  if (!workspaceId || typeof days !== 'number' || Number.isNaN(days)) {
    return getSmallestWorkspaceOfflineMaxDays()
  }
  const storage = safeGetStorage()
  const daysMap = getWorkspaceOfflineDaysMap()
  daysMap[workspaceId] = days

  if (storage) {
    try {
      storage.setItem(WORKSPACE_OFFLINE_DAYS_KEY, JSON.stringify(daysMap))
    } catch {
      // ignore
    }
  }

  const smallest = computeSmallestOfflineMaxDays(daysMap)
  const currentStamp = getOfflineExpiryStamp()
  const updatedStamp: OfflineExpiryStamp = {
    lastOnlineAt: currentStamp?.lastOnlineAt ?? Date.now(),
    maxDays: smallest,
  }
  saveOfflineExpiryStamp(updatedStamp)
  return smallest
}

export function getSmallestWorkspaceOfflineMaxDays(): number {
  const currentStamp = getOfflineExpiryStamp()
  if (currentStamp && typeof currentStamp.maxDays === 'number') {
    return currentStamp.maxDays
  }
  return computeSmallestOfflineMaxDays()
}

/**
 * Proof of server contact: moves lastOnlineAt forward.
 */
export function recordServerContact(timestamp?: number): OfflineExpiryStamp {
  const time = typeof timestamp === 'number' ? timestamp : Date.now()
  const current = getOfflineExpiryStamp()
  const maxDays = current?.maxDays ?? computeSmallestOfflineMaxDays()
  const updated: OfflineExpiryStamp = {
    lastOnlineAt: time,
    maxDays,
  }
  saveOfflineExpiryStamp(updated)
  return updated
}

/**
 * Snapshot proof of contact helper:
 * moves stamp forward ONLY when metadata.fromCache === false.
 */
export function recordSnapshotContact(
  metadata: { fromCache: boolean } | null | undefined,
  timestamp?: number,
): boolean {
  if (metadata && metadata.fromCache === false) {
    recordServerContact(timestamp)
    return true
  }
  return false
}

/**
 * Checks if the device offline time exceeds maxDays.
 */
export function isOfflineExpired(now: number = Date.now()): boolean {
  const stamp = getOfflineExpiryStamp()
  if (!stamp) {
    return false
  }
  const maxDays = stamp.maxDays > 0 ? stamp.maxDays : DEFAULT_OFFLINE_MAX_DAYS
  const elapsedMs = now - stamp.lastOnlineAt
  return elapsedMs > maxDays * ONE_DAY_MS
}

/**
 * Whether the startup sequence must mount memoryLocalCache rather than persistent cache.
 */
export function shouldStartWithMemoryCacheDueToOfflineExpiry(now: number = Date.now()): boolean {
  return isOfflineExpired(now)
}

/**
 * Clears Firestore IndexedDB databases if accessible in the browser.
 */
export async function clearFirestoreIndexedDatabases(): Promise<void> {
  if (typeof window === 'undefined' || !window.indexedDB) return
  try {
    if (typeof window.indexedDB.databases === 'function') {
      const dbs = await window.indexedDB.databases()
      for (const info of dbs) {
        if (
          info.name &&
          (info.name.includes('firestore') ||
            info.name.startsWith('firestore/') ||
            info.name.startsWith('firebase'))
        ) {
          try {
            window.indexedDB.deleteDatabase(info.name)
          } catch {
            // ignore
          }
        }
      }
    }
  } catch {
    // ignore
  }
}

export interface EnforceOfflineExpiryResult {
  expired: boolean
  wiped: boolean
  expiredOutboxCount: number
}

/**
 * Enforces offline expiry:
 * If expired:
 * - drops cached session user
 * - clears count drafts
 * - marks helper and member outbox entries older than the window EXPIRED
 * - clears firestore persistence databases
 */
export async function enforceOfflineExpiry(
  now: number = Date.now(),
): Promise<EnforceOfflineExpiryResult> {
  if (!isOfflineExpired(now)) {
    return { expired: false, wiped: false, expiredOutboxCount: 0 }
  }

  // 1. Drop cached session user
  const sessionStorage = safeGetSessionStorage()
  if (sessionStorage) {
    try {
      sessionStorage.removeItem(SESSION_USER_STORAGE_KEY)
    } catch {
      // ignore
    }
  }

  // 2. Clear count drafts
  const { clearAllCountDrafts } = await import('./helper/helperCountDraftStore')
  clearAllCountDrafts()

  // 3. Mark outbox entries older than the window EXPIRED
  const stamp = getOfflineExpiryStamp()
  const maxDays = stamp?.maxDays ?? DEFAULT_OFFLINE_MAX_DAYS
  const windowMs = maxDays * ONE_DAY_MS
  const expiredMsg = 'Update expired: device was offline longer than the allowed policy window.'

  const [{ expireOldHelperOutboxEntries }, { expireOldMemberOutboxEntries }] = await Promise.all([
    import('./helper/helperOutboxDb'),
    import('./memberOutboxService'),
  ])

  const [hCount, mCount] = await Promise.all([
    expireOldHelperOutboxEntries(windowMs, expiredMsg, now).catch(() => 0),
    expireOldMemberOutboxEntries(windowMs, expiredMsg, now).catch(() => 0),
  ])

  // 4. Wipe Firestore IndexedDB cache
  await clearFirestoreIndexedDatabases()

  return {
    expired: true,
    wiped: true,
    expiredOutboxCount: hCount + mCount,
  }
}
